
import React, { useState, useEffect, useRef } from 'react';
import { defaultQuestions, defaultFolders, defaultUsers } from './data/index'; 
import { QuizState, Question, Folder, QuizRecord, User, FSRSCollection } from './data/types'; 
import { formatTime, getTags } from './utils';
import { Header, Sidebar } from './components/Common';
import { WelcomeView, QuizPlayingView, QuizResultView } from './components/QuizViews';
import { AdminPanel } from './components/AdminPanel';
import { LearningMapView } from './components/LearningMap';
import { SettingsView } from './components/Settings';
import { CardBrowserView } from './components/CardBrowser';
import { MistakeReviewView } from './components/MistakeReview';
import { FavoritesReviewView } from './components/FavoritesReview';
import { AuthView } from './components/Auth';
import { DashboardView } from './components/Dashboard';
import { LectureView } from './components/LectureView';
import { FSRSService } from './services/fsrs';

const TIME_PER_QUESTION = 2 * 60; // 2 minutes per question

// Global Storage Keys (Shared across users)
const QUESTIONS_KEY = 'math-quiz-questions-db-v3'; 
const FOLDERS_KEY = 'math-quiz-folders-db-v3';
const USERS_KEY = 'math-quiz-users-v1';

// Base keys for user-specific data (appended with _userId)
const HISTORY_KEY_BASE = 'math-quiz-history-v1';
const MISTAKES_KEY_BASE = 'math-quiz-mistakes-v1';
const FAVORITES_KEY_BASE = 'math-quiz-favorites-v1';
const FSRS_DATA_KEY_BASE = 'math-quiz-fsrs-v1';
const SETTINGS_KEY_BASE = 'math-quiz-settings-v1';

export default function App() {
  // Global Data
  const [questions, setQuestions] = useState<Question[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [isGlobalDataLoaded, setIsGlobalDataLoaded] = useState(false);

  // Auth State
  const [users, setUsers] = useState<User[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // User-Specific Data
  const [quizHistory, setQuizHistory] = useState<QuizRecord[]>([]);
  const [wrongQuestionIds, setWrongQuestionIds] = useState<number[]>([]);
  const [favoriteQuestionIds, setFavoriteQuestionIds] = useState<number[]>([]);
  const [fsrsData, setFsrsData] = useState<FSRSCollection>({});
  
  // Settings
  const [retentionRate, setRetentionRate] = useState(0.9); // Default 90%

  // Grade Level Management
  const [activeGradeId, setActiveGradeId] = useState<string>('grade-private');

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentView, setCurrentView] = useState<'dashboard' | 'quiz' | 'card-browser' | 'learning-map' | 'admin' | 'settings' | 'mistake-book' | 'favorites' | 'lecture'>('dashboard'); 

  // Lecture View State
  const [currentLectureFolderId, setCurrentLectureFolderId] = useState<string | null>(null);

  const [activeQuizQuestions, setActiveQuizQuestions] = useState<Question[]>([]);
  const [selectedQuizFolderId, setSelectedQuizFolderId] = useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const [gameState, setGameState] = useState<QuizState>({
    status: 'welcome',
    currentQuestionIndex: 0,
    userAnswers: {},
    timeLeft: 0,
  });

  const timerRef = useRef<number | null>(null);
  const fsrsService = useRef(new FSRSService(0.9));

  // Update FSRS service when retention rate changes
  useEffect(() => {
      fsrsService.current.updateRetentionRate(retentionRate);
  }, [retentionRate]);

  // 1. Initial Load: Load Global Data (Questions, Folders, Users)
  useEffect(() => {
    const savedQuestions = localStorage.getItem(QUESTIONS_KEY);
    const savedFolders = localStorage.getItem(FOLDERS_KEY);
    const savedUsers = localStorage.getItem(USERS_KEY);

    if (savedQuestions) {
      try {
        const parsedQ = JSON.parse(savedQuestions);
        const restoredQuestions = parsedQ.map((q: any) => {
            const defaultQ = defaultQuestions.find(dq => dq.id === q.id);
            if (defaultQ && defaultQ.svg) {
                return { ...q, svg: defaultQ.svg };
            }
            return q;
        });
        setQuestions(restoredQuestions);
      } catch (e) {
        setQuestions(defaultQuestions);
      }
    } else {
      setQuestions(defaultQuestions);
    }

    if (savedFolders) {
        try {
            setFolders(JSON.parse(savedFolders));
        } catch(e) {
            setFolders(defaultFolders);
        }
    } else {
        setFolders(defaultFolders);
    }

    if (savedUsers) {
        try {
            setUsers(JSON.parse(savedUsers));
        } catch (e) {
            setUsers(defaultUsers);
        }
    } else {
        setUsers(defaultUsers);
    }

    setIsGlobalDataLoaded(true);
  }, []);

  // 2. Load User Specific Data when currentUser changes
  useEffect(() => {
    if (currentUser) {
        const historyKey = `${HISTORY_KEY_BASE}_${currentUser.id}`;
        const mistakesKey = `${MISTAKES_KEY_BASE}_${currentUser.id}`;
        const favoritesKey = `${FAVORITES_KEY_BASE}_${currentUser.id}`;
        const fsrsKey = `${FSRS_DATA_KEY_BASE}_${currentUser.id}`;
        const settingsKey = `${SETTINGS_KEY_BASE}_${currentUser.id}`;

        const savedHistory = localStorage.getItem(historyKey);
        const savedMistakes = localStorage.getItem(mistakesKey);
        const savedFavorites = localStorage.getItem(favoritesKey);
        const savedFsrs = localStorage.getItem(fsrsKey);
        const savedSettings = localStorage.getItem(settingsKey);

        if (savedHistory) { try { setQuizHistory(JSON.parse(savedHistory)); } catch(e) { setQuizHistory([]); } } 
        else { setQuizHistory([]); }

        if (savedMistakes) { try { setWrongQuestionIds(JSON.parse(savedMistakes)); } catch(e) { setWrongQuestionIds([]); } } 
        else { setWrongQuestionIds([]); }

        if (savedFavorites) { try { setFavoriteQuestionIds(JSON.parse(savedFavorites)); } catch(e) { setFavoriteQuestionIds([]); } } 
        else { setFavoriteQuestionIds([]); }

        if (savedFsrs) { 
            try { 
                const parsed = JSON.parse(savedFsrs); 
                // Restore Date objects from strings
                Object.keys(parsed).forEach(key => {
                    const card = parsed[key];
                    card.due = new Date(card.due);
                    card.last_review = card.last_review ? new Date(card.last_review) : undefined;
                });
                setFsrsData(parsed);
            } catch(e) { setFsrsData({}); } 
        } else { setFsrsData({}); }

        if (savedSettings) {
            try {
                const settings = JSON.parse(savedSettings);
                if (settings.retentionRate) setRetentionRate(settings.retentionRate);
            } catch(e) {}
        }

        // Restore user's last selected grade preference
        if (currentUser.lastActiveGradeId) {
            setActiveGradeId(currentUser.lastActiveGradeId);
        } else {
            setActiveGradeId('grade-private');
        }

        // Reset View State on user switch - Default to Dashboard
        setCurrentView('dashboard');
        setGameState({
          status: 'welcome',
          currentQuestionIndex: 0,
          userAnswers: {},
          timeLeft: 0,
        });
    } else {
        // Clear data when logged out
        setQuizHistory([]);
        setWrongQuestionIds([]);
        setFavoriteQuestionIds([]);
        setFsrsData({});
    }
  }, [currentUser]);

  // 3. Save Global Data when changed
  useEffect(() => {
    if (isGlobalDataLoaded) {
      const dataToSave = questions.map(({ svg, ...rest }) => rest);
      localStorage.setItem(QUESTIONS_KEY, JSON.stringify(dataToSave));
      localStorage.setItem(FOLDERS_KEY, JSON.stringify(folders));
      localStorage.setItem(USERS_KEY, JSON.stringify(users));
    }
  }, [questions, folders, users, isGlobalDataLoaded]);

  // 4. Save User Specific Data when changed
  useEffect(() => {
    if (currentUser) {
        const historyKey = `${HISTORY_KEY_BASE}_${currentUser.id}`;
        const mistakesKey = `${MISTAKES_KEY_BASE}_${currentUser.id}`;
        const favoritesKey = `${FAVORITES_KEY_BASE}_${currentUser.id}`;
        const fsrsKey = `${FSRS_DATA_KEY_BASE}_${currentUser.id}`;
        const settingsKey = `${SETTINGS_KEY_BASE}_${currentUser.id}`;

        localStorage.setItem(historyKey, JSON.stringify(quizHistory));
        localStorage.setItem(mistakesKey, JSON.stringify(wrongQuestionIds));
        localStorage.setItem(favoritesKey, JSON.stringify(favoriteQuestionIds));
        localStorage.setItem(fsrsKey, JSON.stringify(fsrsData));
        localStorage.setItem(settingsKey, JSON.stringify({ retentionRate }));
    }
  }, [quizHistory, wrongQuestionIds, favoriteQuestionIds, fsrsData, retentionRate, currentUser]);

  useEffect(() => {
      setSelectedCategories([]);
  }, [selectedQuizFolderId]);

  // View Switch Termination Logic
  useEffect(() => {
      if (currentView !== 'quiz' && gameState.status === 'playing') {
          // If leaving the quiz page while playing, reset the game
          if (timerRef.current) clearInterval(timerRef.current);
          setGameState({
              status: 'welcome',
              currentQuestionIndex: 0,
              userAnswers: {},
              timeLeft: 0,
          });
      }
  }, [currentView, gameState.status]);

  useEffect(() => {
    if (gameState.status === 'playing' && gameState.timeLeft > 0) {
      timerRef.current = window.setInterval(() => {
        setGameState((prev) => {
          if (prev.timeLeft <= 1) {
            clearInterval(timerRef.current!);
            return { ...prev, status: 'finished', timeLeft: 0 };
          }
          return { ...prev, timeLeft: prev.timeLeft - 1 };
        });
      }, 1000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [gameState.status]);

  // --- Auth Handlers ---
  const handleLogin = (user: User) => {
      setCurrentUser(user);
  };

  const handleRegister = (newUser: User) => {
      setUsers([...users, newUser]);
      setCurrentUser(newUser);
  };

  const handleLogout = () => {
      setCurrentUser(null);
  };
  
  const handleUpdateUser = (updatedUser: User) => {
      const newUsers = users.map(u => u.id === updatedUser.id ? updatedUser : u);
      setUsers(newUsers);
      setCurrentUser(updatedUser);
      localStorage.setItem(USERS_KEY, JSON.stringify(newUsers));
  };

  // --- Grade Change Handler with Persistence ---
  const handleGradeChange = (newGradeId: string) => {
      setActiveGradeId(newGradeId);
      if (currentUser) {
          const updatedUser = { ...currentUser, lastActiveGradeId: newGradeId };
          handleUpdateUser(updatedUser);
      }
  };

  // --- Quiz Logic ---
  const getQuestionsInFolderRecursive = (folderId: string | null): Question[] => {
      let result = questions.filter(q => (q.folderId || null) === (folderId || null));
      const subFolders = folders.filter(f => (f.parentId || null) === (folderId || null));
      subFolders.forEach((sub) => {
          result = [...result, ...getQuestionsInFolderRecursive(sub.id)];
      });
      return result;
  };

  const startQuiz = (targetFolderId: string | null = null, explicitQuestions: Question[] | null = null, isReviewMode: boolean = false, questionCount: number = 20) => {
    let questionsToPlay = [];

    if (explicitQuestions) {
        questionsToPlay = explicitQuestions;
    } else {
        const folderId = targetFolderId !== null ? targetFolderId : (selectedQuizFolderId || activeGradeId);
        if (targetFolderId !== null) setSelectedQuizFolderId(targetFolderId);

        if (folderId === activeGradeId) {
             questionsToPlay = getQuestionsInFolderRecursive(activeGradeId);
        } else {
             questionsToPlay = getQuestionsInFolderRecursive(folderId);
        }

        if (targetFolderId !== null) {
            setSelectedCategories([]);
        } else if (selectedCategories.length > 0) {
            questionsToPlay = questionsToPlay.filter(q => {
                const qTags = getTags(q.category);
                return selectedCategories.some(selectedCat => qTags.includes(selectedCat));
            });
        }
    }

    if (questionsToPlay.length === 0) {
        alert("此範圍內沒有題目！");
        return;
    }

    // Shuffle and Limit
    if (!isReviewMode) {
        questionsToPlay = [...questionsToPlay].sort(() => Math.random() - 0.5);
        if (!explicitQuestions) {
            // Only slice if it's a generated quiz, not an explicit set (like mistakes review)
            questionsToPlay = questionsToPlay.slice(0, questionCount);
        }
    }

    const calculatedTime = questionsToPlay.length * TIME_PER_QUESTION;

    setActiveQuizQuestions(questionsToPlay);
    setGameState({
      status: 'playing',
      currentQuestionIndex: 0,
      userAnswers: {},
      timeLeft: calculatedTime,
      isReviewMode: isReviewMode
    });
    
    if (currentView !== 'quiz') setCurrentView('quiz');
    setIsSidebarOpen(false);
  };

  const startLecture = (folderId: string) => {
      setCurrentLectureFolderId(folderId);
      setCurrentView('lecture');
  };

  const resetQuiz = () => {
    setGameState({
      status: 'welcome',
      currentQuestionIndex: 0,
      userAnswers: {},
      timeLeft: 0,
    });
  };

  const handleOptionSelect = (optionIndex: number) => {
    setGameState((prev) => {
      const currentQuestionId = activeQuizQuestions[prev.currentQuestionIndex].id;
      return {
        ...prev,
        userAnswers: {
          ...prev.userAnswers,
          [currentQuestionId]: optionIndex,
        },
      };
    });
  };

  const nextQuestion = () => {
    if (gameState.currentQuestionIndex < activeQuizQuestions.length - 1) {
      setGameState((prev) => ({
        ...prev,
        currentQuestionIndex: prev.currentQuestionIndex + 1,
      }));
    } else {
      finishQuiz();
    }
  };
  
  const prevQuestion = () => {
    if (gameState.currentQuestionIndex > 0) {
        setGameState((prev) => ({
          ...prev,
          currentQuestionIndex: prev.currentQuestionIndex - 1,
        }));
      }
  }

  const finishQuiz = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    const score = calculateScore();
    const totalPoints = activeQuizQuestions.length * 10;
    
    // --- Auto-collect wrong questions (User Specific) ---
    const newWrongIds = new Set(wrongQuestionIds);
    const newFsrsData = { ...fsrsData };
    let hasFsrsUpdates = false;

    activeQuizQuestions.forEach(q => {
        const userAnswer = gameState.userAnswers[q.id];
        const isCorrect = userAnswer === q.correctAnswerIndex;

        if (userAnswer !== undefined && !isCorrect) {
            newWrongIds.add(q.id);
        }

        // --- FSRS Logic ---
        if (gameState.isReviewMode || !isCorrect || newFsrsData[q.id]) {
            let card = newFsrsData[q.id];
            if (!card) {
                card = fsrsService.current.createCard();
            }
            if (userAnswer !== undefined) {
                const updatedCard = fsrsService.current.reviewCard(card, isCorrect);
                newFsrsData[q.id] = updatedCard;
                hasFsrsUpdates = true;
            }
        }
    });

    setWrongQuestionIds(Array.from(newWrongIds));
    if (hasFsrsUpdates) {
        setFsrsData(newFsrsData);
    }
    // ------------------------------------

    if (activeQuizQuestions.length > 0 && !gameState.isReviewMode) {
        const newRecord: QuizRecord = {
            id: Date.now().toString(),
            date: new Date().toISOString(),
            folderId: selectedQuizFolderId || activeGradeId,
            score: score,
            totalPoints: totalPoints
        };
        setQuizHistory(prev => [...prev, newRecord]);
    }

    setGameState((prev) => ({ ...prev, status: 'finished' }));
  };

  const calculateScore = () => {
    let score = 0;
    activeQuizQuestions.forEach((q) => {
      if (gameState.userAnswers[q.id] === q.correctAnswerIndex) {
        score += 10;
      }
    });
    return score;
  };

  const handleResetData = () => {
      localStorage.removeItem(QUESTIONS_KEY);
      localStorage.removeItem(FOLDERS_KEY);
      localStorage.removeItem(USERS_KEY);
      if (currentUser) {
          localStorage.removeItem(`${HISTORY_KEY_BASE}_${currentUser.id}`);
          localStorage.removeItem(`${MISTAKES_KEY_BASE}_${currentUser.id}`);
          localStorage.removeItem(`${FAVORITES_KEY_BASE}_${currentUser.id}`);
          localStorage.removeItem(`${FSRS_DATA_KEY_BASE}_${currentUser.id}`);
          localStorage.removeItem(`${SETTINGS_KEY_BASE}_${currentUser.id}`);
      }
      
      setQuestions(defaultQuestions);
      setFolders(defaultFolders);
      setUsers(defaultUsers);
      setFsrsData({});
      setCurrentUser(null);
  };

  const handleClearQuestions = () => {
      setQuestions([]);
      setCurrentView('dashboard');
      setGameState({
        status: 'welcome',
        currentQuestionIndex: 0,
        userAnswers: {},
        timeLeft: 0,
      });
  };

  // --- Mistake Notebook Logic ---
  const startQuizWithMistakes = (specificIds?: number[]) => {
      const idsToPlay = specificIds || wrongQuestionIds;
      const mistakeQuestions = questions.filter(q => idsToPlay.includes(q.id));
      startQuiz(null, mistakeQuestions, true); // true for isReviewMode
  };

  const removeFromMistakes = (id: number) => {
      setWrongQuestionIds(prev => prev.filter(wid => wid !== id));
  };

  // --- Favorites Logic ---
  const toggleFavorite = (id: number) => {
      setFavoriteQuestionIds(prev => {
          if (prev.includes(id)) {
              return prev.filter(fid => fid !== id);
          } else {
              return [...prev, id];
          }
      });
  };

  const startQuizWithFavorites = () => {
      const favQuestions = questions.filter(q => favoriteQuestionIds.includes(q.id));
      startQuiz(null, favQuestions, false);
  };

  const getPageTitle = () => {
      if (currentView === 'dashboard') return '個人儀表板';
      if (currentView === 'admin') return '題庫資源管理';
      if (currentView === 'settings') return '系統設定';
      if (currentView === 'learning-map') return '學習地圖';
      if (currentView === 'card-browser') return '卡片瀏覽';
      if (currentView === 'mistake-book') return '錯題本';
      if (currentView === 'favorites') return '我的最愛';
      if (currentView === 'lecture') return '重點講義';
      if (gameState.status === 'playing') return '測驗進行中';
      if (gameState.status === 'finished') return '測驗結果';
      return '測驗首頁';
  };

  // --- Conditional Rendering based on Auth ---
  if (!currentUser) {
      return (
          <AuthView 
              users={users} 
              onLogin={handleLogin} 
              onRegister={handleRegister} 
          />
      );
  }

  return (
    <div className="flex h-screen overflow-hidden font-sans text-gray-900 bg-gray-50">
        <Sidebar 
            isOpen={isSidebarOpen} 
            setIsOpen={setIsSidebarOpen} 
            currentView={currentView} 
            setCurrentView={setCurrentView}
            folders={folders}
            activeGradeId={activeGradeId}
            setActiveGradeId={handleGradeChange}
            currentUser={currentUser}
            onLogout={handleLogout}
            onUpdateUser={handleUpdateUser}
        />

        <div className="flex-1 flex flex-col h-full overflow-hidden relative">
            <Header 
                title={getPageTitle()}
                onMenuClick={() => setIsSidebarOpen(true)}
            />

            <main className="flex-1 overflow-hidden flex flex-col relative">
                {currentView === 'dashboard' && (
                    <DashboardView
                        user={currentUser}
                        quizHistory={quizHistory}
                        wrongQuestionIds={wrongQuestionIds}
                        favoriteQuestionIds={favoriteQuestionIds}
                        onNavigate={(view: any) => setCurrentView(view)}
                        folders={folders}
                    />
                )}

                {currentView === 'lecture' && (
                    <LectureView 
                        folder={folders.find(f => f.id === currentLectureFolderId) || folders[0]}
                        onBack={() => setCurrentView('learning-map')}
                        onStartQuiz={() => startQuiz(currentLectureFolderId)}
                    />
                )}

                {currentView === 'quiz' && (
                    <>
                        {gameState.status === 'welcome' && (
                            <WelcomeView 
                                startQuiz={(count: number) => startQuiz(null, null, false, count)}
                                folders={folders}
                                questions={questions}
                                rootId={activeGradeId}
                                selectedQuizFolderId={selectedQuizFolderId}
                                setSelectedQuizFolderId={setSelectedQuizFolderId}
                                selectedCategories={selectedCategories}
                                setSelectedCategories={setSelectedCategories}
                            />
                        )}
                        {gameState.status === 'playing' && (
                            <QuizPlayingView 
                                gameState={gameState}
                                questions={activeQuizQuestions}
                                formatTime={formatTime}
                                handleOptionSelect={handleOptionSelect}
                                prevQuestion={prevQuestion}
                                nextQuestion={nextQuestion}
                                finishQuiz={finishQuiz}
                                favoriteQuestionIds={favoriteQuestionIds}
                                toggleFavorite={toggleFavorite}
                            />
                        )}
                        {gameState.status === 'finished' && (
                            <QuizResultView 
                                gameState={gameState}
                                questions={activeQuizQuestions}
                                resetQuiz={resetQuiz}
                                calculateScore={calculateScore}
                            />
                        )}
                    </>
                )}
                
                {currentView === 'mistake-book' && (
                    <MistakeReviewView
                        wrongQuestions={wrongQuestionIds}
                        questions={questions}
                        removeFromMistakes={removeFromMistakes}
                        startQuizWithMistakes={startQuizWithMistakes}
                        fsrsData={fsrsData}
                    />
                )}

                {currentView === 'favorites' && (
                    <FavoritesReviewView
                        favoriteQuestions={favoriteQuestionIds}
                        questions={questions}
                        toggleFavorite={toggleFavorite}
                        startQuizWithFavorites={startQuizWithFavorites}
                    />
                )}

                {currentView === 'learning-map' && (
                    <LearningMapView 
                        folders={folders}
                        rootId={activeGradeId}
                        quizHistory={quizHistory}
                        onStartFolderQuiz={(folderId: string) => startQuiz(folderId)}
                        onReadLecture={startLecture}
                    />
                )}

                {currentView === 'card-browser' && (
                    <CardBrowserView
                        folders={folders}
                        questions={questions}
                        rootId={activeGradeId}
                        favoriteQuestionIds={favoriteQuestionIds}
                        toggleFavorite={toggleFavorite}
                    />
                )}

                {currentView === 'admin' && (
                    <AdminPanel 
                        questions={questions} 
                        folders={folders} 
                        rootId={activeGradeId}
                        setQuestions={setQuestions} 
                        setFolders={setFolders} 
                    />
                )}

                {currentView === 'settings' && (
                    <SettingsView 
                        onResetData={handleResetData}
                        onClearQuestions={handleClearQuestions}
                        retentionRate={retentionRate}
                        setRetentionRate={setRetentionRate}
                    />
                )}
            </main>
        </div>
    </div>
  );
}
